import type { ResultOf } from "@/graphql";
import type { PropsWithChildren } from "react";
import { createContext, useContext, useEffect, useMemo } from "react";
import type MeParts from "@/api/fragments/me-parts";
import { MeQuery } from "@/api/queries/user";
import * as m from "@/paraglide/messages.js";
import { setLocale } from "@/paraglide/runtime";
import { useQuery } from "@apollo/client";
import { useAuth0 } from "@auth0/auth0-react";
import toast from "react-hot-toast";

export let language: "sv" | "en" = "sv";

// MeQuery also selects the full company for the cache. The app reads that through useCompany, not useMe.
export type Me = NonNullable<ResultOf<typeof MeParts>>;

const MeContext = createContext<Me | null>(null);

function MeProvider({ children }: PropsWithChildren) {
  const { isAuthenticated } = useAuth0();
  const { data } = useQuery(MeQuery, { skip: !isAuthenticated });
  const me = data?.me;

  useEffect(() => {
    language = (me?.language as "sv" | "en") || "sv";
    // Without reload: false, Paraglide 2 reloads the page whenever the locale changes.
    void setLocale(language, { reload: false });
    document.documentElement.lang = language;
  }, [me?.language]);

  // Set just before the reload that follows a refused view-as id.
  useEffect(() => {
    if (me?.id && sessionStorage.getItem("viewAsRefused")) {
      sessionStorage.removeItem("viewAsRefused");
      toast.error(m.view_as_user_refused(), { duration: 10000 });
    }
  }, [me?.id]);

  const initialMe = useMemo<Me>(
    () => ({
      __typename: "User",
      id: "",
      firstName: "",
      lastName: "",
      email: "",
      contactEmail: "",
      phoneNumber: "",
      language: "sv",
      profileImage: null,
      jobTitle: "",
      company: {
        __typename: "Company",
        id: "",
        name: "",
        language: "en",
        type: null,
        profileImage: null,
        childCompanies: [],
        parentCompany: {
          __typename: "Company",
          id: "",
          name: "",
        },
        enabledFeatures: {
          __typename: "EnabledFeatures",
          id: "",
          assignments: false,
          calendar: false,
          consultantProjects: false,
          contracts: false,
          screeningForms: false,
          referenceForms: false,
          statusDeadlines: false,
          trustcruit: false,
          interviewBooking: false,
          applicationsRankedWithAI: false,
          alvaLabs: false,
          tengai: false,
          interviewBookingGroups: false,
          allApplicationsPage: false,
          videoInApplications: false,
          aiGeneration: false,
          mediaLibrary: false,
          careerPage: false,
          jobtip: false,
          evaluationForm: false,
        },
      },
      privilegeLevel: "USER",
      subscribedMailNotificationTypes: [],
      subscribedToNotificationsStatus: "NONE",
    }),
    [],
  );

  return (
    <MeContext.Provider value={me || initialMe}>{children}</MeContext.Provider>
  );
}

const useMe = () => {
  const context = useContext(MeContext);

  if (!context) {
    throw new Error("useMe must be used within a MeProvider");
  }

  return context;
};

export { MeProvider, useMe };
